/**
 * Phase 124.W + 124.W.1 — DiagramImage.
 *
 * Drop-in replacement for the markdown `img` renderer that:
 *  • Detects `/api/diagrams/{id}.png` srcs and surfaces a small
 *    "Regenerate" button overlaid on the image (top-right, hover).
 *  • Surfaces a pencil button that opens an inline popover where the
 *    teacher can edit the diagram description before retrying. The
 *    edited prompt is persisted server-side so subsequent plain
 *    regen clicks reuse the new text.
 *  • Hits POST /api/diagrams/{id}/regenerate, then cache-busts the
 *    immutable PNG by appending `?v={regenerated_count}` to the src.
 *  • Pass-through for any non-diagram image (e.g. raster assets the
 *    LLM occasionally hot-links in markdown).
 */
import { useEffect, useRef, useState } from "react";
import { Loader2, Pencil, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";

const apiBase = () => `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

// Matches both absolute (`https://.../api/diagrams/diag-abc.png`) and
// relative (`/api/diagrams/diag-abc.png`) srcs the renderer might
// emit, plus an optional `?v=...` cache-buster the regenerate flow
// appends. The first capture group is the diagram id.
const DIAGRAM_SRC_RE = /\/api\/diagrams\/([a-zA-Z0-9_-]+)\.png(?:\?[^"]*)?$/;

export default function DiagramImage({ src, alt, ...rest }) {
  const initialMatch = typeof src === "string" ? src.match(DIAGRAM_SRC_RE) : null;
  const diagramId = initialMatch ? initialMatch[1] : null;
  const [version, setVersion] = useState(0);
  const [busy, setBusy] = useState(false);

  // Inline-popover state for the pencil flow.
  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [loadingDraft, setLoadingDraft] = useState(false);
  const fetchedFor = useRef(null);

  // Pull the current description into the textarea the first time the
  // teacher opens the popover. We don't ship the description in the
  // markdown (it can be long + LLM-y) so we hit the metadata endpoint
  // on demand. One-shot per diagram so clearing the textarea doesn't
  // trigger a re-fetch that repopulates the field.
  useEffect(() => {
    if (!editOpen || !diagramId) return;
    if (fetchedFor.current === diagramId) return;
    setLoadingDraft(true);
    (async () => {
      try {
        const res = await fetch(`${apiBase()}/diagrams/${diagramId}`, {
          credentials: "include",
        });
        if (!res.ok) throw new Error(`Couldn't load description (${res.status})`);
        const data = await res.json();
        setDraft(data.description || "");
        fetchedFor.current = diagramId;
      } catch (e) {
        toast.error(e?.message || "Couldn't load the diagram description.");
      } finally {
        setLoadingDraft(false);
      }
    })();
  }, [editOpen, diagramId]);

  // Non-diagram images render unchanged so we don't accidentally
  // wrap unrelated markdown imagery.
  if (!diagramId) {
    return <img src={src} alt={alt} {...rest} />;
  }

  const effectiveSrc = version > 0 ? `${src.split("?")[0]}?v=${version}` : src;

  const callRegenerate = async (overrideDescription) => {
    setBusy(true);
    try {
      const init = {
        method: "POST",
        credentials: "include",
      };
      if (overrideDescription && overrideDescription.trim()) {
        init.headers = { "Content-Type": "application/json" };
        init.body = JSON.stringify({ description: overrideDescription.trim() });
      }
      const res = await fetch(`${apiBase()}/diagrams/${diagramId}/regenerate`, init);
      if (res.status === 401 || res.status === 403) {
        toast.error("Sign in to regenerate diagrams.");
        return false;
      }
      if (res.status === 502) {
        toast.error("Image provider returned no image. Try again in a moment.");
        return false;
      }
      if (!res.ok) throw new Error(`Regenerate failed (${res.status})`);
      const data = await res.json();
      setVersion(data.regenerated_count || Date.now());
      if (overrideDescription && data.description) {
        // Server may have trimmed / canonicalised — keep the popover
        // in sync.
        setDraft(data.description);
      }
      toast.success("Diagram regenerated.");
      return true;
    } catch (e) {
      toast.error(e?.message || "Regenerate failed.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const onRegenerate = () => { callRegenerate(); };

  const onRegenerateWithEdit = async () => {
    if (!draft.trim()) {
      toast.error("Description can't be empty.");
      return;
    }
    const ok = await callRegenerate(draft);
    if (ok) setEditOpen(false);
  };

  return (
    <span
      className="relative inline-block group"
      data-testid={`diagram-tile-${diagramId}`}
    >
      <img
        src={effectiveSrc}
        alt={alt}
        className="max-w-full h-auto rounded-lg border border-[#4a6984]/15"
        {...rest}
      />
      <span className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition">
        <Popover open={editOpen} onOpenChange={setEditOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              title="Edit description and regenerate"
              data-testid={`diagram-edit-${diagramId}`}
              className="inline-flex items-center px-2 py-1 rounded-full bg-white/95 border border-[#4a6984]/20 text-[10px] font-bold text-[#1c2d3d] shadow-sm hover:bg-[#96ded1]/30 transition"
            >
              <Pencil className="h-3 w-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            sideOffset={8}
            className="w-80 p-3 space-y-2"
            data-testid={`diagram-edit-popover-${diagramId}`}
          >
            <p className="text-[10px] uppercase tracking-wide font-extrabold text-[#4a6984]/80">
              Edit diagram description
            </p>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={4}
              disabled={loadingDraft || busy}
              placeholder={loadingDraft ? "Loading…" : "Describe the diagram you want…"}
              data-testid={`diagram-edit-textarea-${diagramId}`}
              className="w-full px-2.5 py-2 rounded border border-[#4a6984]/20 text-xs text-[#1c2d3d] resize-none focus:outline-none focus:ring-2 focus:ring-[#96ded1]"
            />
            <p className="text-[10px] text-[#4a6984]/70 italic leading-snug">
              Tip: add style cues like &ldquo;more cartoony&rdquo; or &ldquo;show the switch open&rdquo; — your edit is saved for future regenerations.
            </p>
            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setEditOpen(false)}
                disabled={busy}
                data-testid={`diagram-edit-cancel-${diagramId}`}
                className="text-[11px] font-bold text-[#4a6984]/80 hover:text-[#1c2d3d] px-2 py-1"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onRegenerateWithEdit}
                disabled={busy || loadingDraft || !draft.trim()}
                data-testid={`diagram-edit-regenerate-${diagramId}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-[11px] font-extrabold text-[#1c2d3d] bg-[#96ded1] hover:bg-[#7fd0c1] disabled:opacity-50 transition"
              >
                {busy ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Regenerating…
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-3 w-3" />
                    Save & regenerate
                  </>
                )}
              </button>
            </div>
          </PopoverContent>
        </Popover>
        <button
          type="button"
          onClick={onRegenerate}
          disabled={busy}
          title="Regenerate diagram"
          data-testid={`diagram-regenerate-${diagramId}`}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/95 border border-[#4a6984]/20 text-[10px] font-bold text-[#1c2d3d] shadow-sm hover:bg-[#96ded1]/30 transition disabled:opacity-100"
        >
          {busy ? (
            <>
              <Loader2 className="h-3 w-3 animate-spin" />
              Regenerating…
            </>
          ) : (
            <>
              <RefreshCw className="h-3 w-3" />
              Regenerate
            </>
          )}
        </button>
      </span>
    </span>
  );
}
